import { cn } from '@/shared/lib';
import { useRTL } from '@/shared/hooks';
import { useTranslation } from 'react-i18next';
import { useActualiseTakeoutTimeSlots } from './hooks';
import { forwardRef, useMemo } from 'react';
import { Typography } from '@/shared/components';
import { ClientLocationDto } from '@/app/store/api/restApi';
import { BasketTakeoutPersonalInfo } from './BasketTakeoutPersonalInfo';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/shadcn';
import {
  clearErrors,
  setNameError,
  setPhoneError,
  useAppDispatch,
  useAppSelector,
  CustomerTabsEnum,
  setSelectedCustomerTab,
} from '@/app/store';

interface BasketTakeoutProps {
  locations: ClientLocationDto[];
}

export const BasketTakeout = forwardRef<HTMLDivElement, BasketTakeoutProps>(({ locations }, ref) => {
  const isRTL = useRTL();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('basket');
  const selectedCustomerTab = useAppSelector((state) => state.orderAnotherPerson.selectedCustomerTab);
  const isOrderForAnotherPersonEnabled = useAppSelector(
    (state) => state.initial.settings.isOrderForAnotherPersonEnabled,
  );

  const mytakeoutAddress = useAppSelector((state) => state.order.takeoutAddress);
  const anotherTakeoutAddress = useAppSelector((state) => state.orderAnotherPerson.takeoutAddress);

  const { takeoutAddress, currentLocation } = useMemo(() => {
    const takeoutAddress = selectedCustomerTab === CustomerTabsEnum.Myself ? mytakeoutAddress : anotherTakeoutAddress;
    const currentLocation = locations.find((l) => l.id === takeoutAddress);
    return { takeoutAddress, currentLocation };
  }, [anotherTakeoutAddress, locations, mytakeoutAddress, selectedCustomerTab]);

  useActualiseTakeoutTimeSlots(currentLocation);

  return (
    <div className='flex flex-col' ref={ref}>
      <Typography variant='h3' className={cn('mb-4', isRTL && 'text-right')}>
        {t('pickup.title')}
      </Typography>

      <Tabs
        value={selectedCustomerTab}
        dir={isRTL ? 'rtl' : 'ltr'}
        onValueChange={(v) => {
          dispatch(clearErrors(undefined));
          dispatch(setNameError(undefined));
          dispatch(setPhoneError(undefined));
          dispatch(setSelectedCustomerTab(v as CustomerTabsEnum));
        }}
        className='gap-0'
      >
        {isOrderForAnotherPersonEnabled && (
          <TabsList>
            <TabsTrigger value={CustomerTabsEnum.Myself}>{t('order.myself')}</TabsTrigger>

            <TabsTrigger value={CustomerTabsEnum.Another}>{t('order.another')}</TabsTrigger>
          </TabsList>
        )}

        <TabsContent value={CustomerTabsEnum.Myself} className={!isOrderForAnotherPersonEnabled ? 'mt-0' : ''}>
          <BasketTakeoutPersonalInfo
            locations={locations}
            takeoutAddress={takeoutAddress}
            currentLocation={currentLocation}
          />
        </TabsContent>

        <TabsContent value={CustomerTabsEnum.Another}>
          <BasketTakeoutPersonalInfo
            isOrderAnotherPerson
            locations={locations}
            takeoutAddress={takeoutAddress}
            currentLocation={currentLocation}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
});
